/**
 * Department Service
 * Handles all department management API operations
 * 
 * @module services/departmentService
 * @requires services/api/BaseService
 */

import { BaseService } from './api';

/**
 * Department Service Class
 * Provides methods for department operations, team management, and performance metrics
 * 
 * @class DepartmentService
 * @extends BaseService
 * 
 * @example
 * import departmentService from './services/departmentService';
 * 
 * // Get all departments
 * const departments = await departmentService.getAll();
 * 
 * // Get department performance
 * const performance = await departmentService.getPerformance('dept-123');
 */
class DepartmentService extends BaseService {
  constructor() {
    super('/departments');
  }

  /**
   * Get all departments with pagination and filters
   * 
   * @param {Object} params - Query parameters
   * @param {number} [params.page=1] - Page number
   * @param {number} [params.limit=20] - Items per page
   * @param {string} [params.status] - Filter by status (active, inactive)
   * @param {string} [params.location] - Filter by location
   * @param {string} [params.search] - Search query
   * @param {string} [params.sortBy] - Sort field
   * @param {string} [params.sortOrder] - Sort order (asc, desc)
   * @returns {Promise<{data: Array, total: number, page: number, limit: number}>}
   */
  async getAll(params = {}) {
    return this.get('', { params });
  }

  /**
   * Get department by ID
   * 
   * @param {string} departmentId - Department ID
   * @returns {Promise<Object>} Department details
   */
  async getById(departmentId) {
    return this.get(`/${departmentId}`);
  }

  /**
   * Create new department
   * 
   * @param {Object} departmentData - Department data
   * @param {string} departmentData.name - Department name
   * @param {string} departmentData.code - Department code
   * @param {string} [departmentData.description] - Description
   * @param {string} [departmentData.managerId] - Manager user ID
   * @param {string} [departmentData.location] - Physical location
   * @param {Object} [departmentData.budget] - Department budget
   * @param {Object} [departmentData.contact] - Contact information
   * @returns {Promise<Object>} Created department
   */
  async create(departmentData) {
    return this.post('', departmentData);
  }

  /**
   * Update department
   * 
   * @param {string} departmentId - Department ID
   * @param {Object} departmentData - Updated department data
   * @returns {Promise<Object>} Updated department
   */
  async update(departmentId, departmentData) {
    return this.put(`/${departmentId}`, departmentData);
  }

  /**
   * Delete department
   * 
   * @param {string} departmentId - Department ID
   * @returns {Promise<void>}
   */
  async delete(departmentId) {
    return this.delete(`/${departmentId}`);
  }

  /**
   * Search departments with cancellation
   * 
   * @param {string} query - Search query
   * @param {Object} [options={}] - Search options
   * @param {number} [options.limit=10] - Max results
   * @returns {Promise<Array>} Matching departments
   */
  async search(query, options = {}) {
    return this.getWithCancel('/search', 'department-search', {
      params: { q: query, ...options }
    });
  }

  /**
   * Get department team members
   * 
   * @param {string} departmentId - Department ID
   * @param {Object} [params={}] - Query parameters
   * @param {string} [params.role] - Filter by role
   * @param {string} [params.status] - Filter by status
   * @returns {Promise<Array>} Team members
   */
  async getTeamMembers(departmentId, params = {}) {
    return this.get(`/${departmentId}/team`, { params });
  }

  /**
   * Add team member to department
   * 
   * @param {string} departmentId - Department ID
   * @param {Object} memberData - Member data
   * @param {string} memberData.userId - User ID
   * @param {string} [memberData.position] - Position/role in department
   * @param {string} [memberData.startDate] - Start date
   * @returns {Promise<Object>} Updated department
   */
  async addTeamMember(departmentId, memberData) {
    return this.post(`/${departmentId}/team`, memberData);
  }

  /**
   * Remove team member from department
   * 
   * @param {string} departmentId - Department ID
   * @param {string} userId - User ID
   * @returns {Promise<Object>} Updated department
   */
  async removeTeamMember(departmentId, userId) {
    return this.delete(`/${departmentId}/team/${userId}`);
  }

  /**
   * Update team member role/position
   * 
   * @param {string} departmentId - Department ID
   * @param {string} userId - User ID
   * @param {Object} updateData - Update data
   * @param {string} [updateData.position] - New position
   * @param {Object} [updateData.responsibilities] - Responsibilities
   * @returns {Promise<Object>} Updated team member
   */
  async updateTeamMember(departmentId, userId, updateData) {
    return this.patch(`/${departmentId}/team/${userId}`, updateData);
  }

  /**
   * Assign department manager
   * 
   * @param {string} departmentId - Department ID
   * @param {string} managerId - User ID of new manager
   * @returns {Promise<Object>} Updated department
   */
  async assignManager(departmentId, managerId) {
    return this.patch(`/${departmentId}/manager`, { managerId });
  }

  /**
   * Get department performance metrics
   * 
   * @param {string} departmentId - Department ID
   * @param {Object} [params={}] - Query parameters
   * @param {string} [params.period='month'] - Time period
   * @param {string} [params.startDate] - Start date
   * @param {string} [params.endDate] - End date
   * @returns {Promise<Object>} Performance metrics
   */
  async getPerformance(departmentId, params = {}) {
    return this.get(`/${departmentId}/performance`, { params });
  }

  /**
   * Get department budget tracking
   * 
   * @param {string} departmentId - Department ID
   * @param {Object} [params={}] - Query parameters
   * @param {number} [params.fiscalYear] - Fiscal year
   * @returns {Promise<Object>} Budget tracking data
   */
  async getBudgetTracking(departmentId, params = {}) {
    return this.get(`/${departmentId}/budget-tracking`, { params });
  }

  /**
   * Get department operational metrics
   * 
   * @param {string} departmentId - Department ID
   * @param {Object} [params={}] - Query parameters
   * @param {string} [params.metric] - Specific metric to retrieve
   * @returns {Promise<Object>} Operational metrics
   */
  async getOperationalMetrics(departmentId, params = {}) {
    return this.get(`/${departmentId}/operational-metrics`, { params });
  }

  /**
   * Get department KPIs
   * 
   * @param {string} departmentId - Department ID
   * @param {Object} [params={}] - Query parameters
   * @param {string} [params.period='month'] - Time period
   * @returns {Promise<Array>} KPI data
   */
  async getKPIs(departmentId, params = {}) {
    return this.get(`/${departmentId}/kpis`, { params });
  }

  /**
   * Update department KPI targets
   * 
   * @param {string} departmentId - Department ID
   * @param {Object} kpiData - KPI targets
   * @returns {Promise<Object>} Updated KPIs
   */
  async updateKPIs(departmentId, kpiData) {
    return this.put(`/${departmentId}/kpis`, kpiData);
  }

  /**
   * Get department tasks/projects
   * 
   * @param {string} departmentId - Department ID
   * @param {Object} [params={}] - Query parameters
   * @param {string} [params.status] - Filter by status
   * @param {string} [params.priority] - Filter by priority
   * @returns {Promise<Array>} Tasks/projects
   */
  async getTasks(departmentId, params = {}) {
    return this.get(`/${departmentId}/tasks`, { params });
  }

  /**
   * Get department activity log
   * 
   * @param {string} departmentId - Department ID
   * @param {Object} [params={}] - Query parameters
   * @param {number} [params.page=1] - Page number
   * @param {number} [params.limit=20] - Items per page
   * @param {string} [params.startDate] - Start date
   * @param {string} [params.endDate] - End date
   * @returns {Promise<Array>} Activity log
   */
  async getActivityLog(departmentId, params = {}) {
    return this.get(`/${departmentId}/activity`, { params });
  }

  /**
   * Get department notifications
   * 
   * @param {string} departmentId - Department ID
   * @param {Object} [params={}] - Query parameters
   * @param {boolean} [params.unread] - Only unread notifications
   * @param {string} [params.type] - Filter by type
   * @returns {Promise<Array>} Notifications
   */
  async getNotifications(departmentId, params = {}) {
    return this.get(`/${departmentId}/notifications`, { params });
  }

  /**
   * Send notification to department
   * 
   * @param {string} departmentId - Department ID
   * @param {Object} notificationData - Notification data
   * @param {string} notificationData.title - Title
   * @param {string} notificationData.message - Message
   * @param {string} [notificationData.priority] - Priority level
   * @param {Array<string>} [notificationData.recipients] - Specific recipients (user IDs)
   * @returns {Promise<Object>} Notification result
   */
  async sendNotification(departmentId, notificationData) {
    return this.post(`/${departmentId}/notifications`, notificationData);
  }

  /**
   * Get department hierarchy
   * 
   * @param {string} departmentId - Department ID
   * @returns {Promise<Object>} Hierarchical structure
   */
  async getHierarchy(departmentId) {
    return this.get(`/${departmentId}/hierarchy`);
  }

  /**
   * Get department analytics
   * 
   * @param {string} departmentId - Department ID
   * @param {Object} [params={}] - Query parameters
   * @param {string} [params.metric] - Specific metric
   * @param {string} [params.period='month'] - Time period
   * @returns {Promise<Object>} Analytics data
   */
  async getAnalytics(departmentId, params = {}) {
    return this.get(`/${departmentId}/analytics`, { params });
  }

  /**
   * Compare departments
   * 
   * @param {Object} comparisonData - Comparison data
   * @param {Array<string>} comparisonData.departmentIds - Department IDs to compare
   * @param {string} [comparisonData.metric] - Metric to compare
   * @param {string} [comparisonData.period] - Time period
   * @returns {Promise<Object>} Comparison data
   */
  async compare(comparisonData) {
    return this.post('/compare', comparisonData);
  }

  /**
   * Get all departments overview
   * 
   * @param {Object} [params={}] - Query parameters
   * @returns {Promise<Object>} Departments overview
   */
  async getOverview(params = {}) {
    return this.get('/overview', { params });
  }

  /**
   * Get department reports
   * 
   * @param {string} departmentId - Department ID
   * @param {Object} [params={}] - Query parameters
   * @param {string} [params.reportType] - Type of report
   * @param {string} [params.startDate] - Start date
   * @param {string} [params.endDate] - End date
   * @returns {Promise<Array>} Reports
   */
  async getReports(departmentId, params = {}) {
    return this.get(`/${departmentId}/reports`, { params });
  }

  /**
   * Export department data
   * 
   * @param {string} departmentId - Department ID
   * @param {Object} [params={}] - Export parameters
   * @param {string} [params.format='xlsx'] - Export format
   * @param {string} [params.dataType] - Type of data to export
   * @returns {Promise<Blob>} Export file
   */
  async export(departmentId, params = {}) {
    return this.get(`/${departmentId}/export`, {
      params,
      responseType: 'blob'
    });
  }

  /**
   * Get department statistics
   * 
   * @returns {Promise<Object>} Department statistics
   */
  async getStatistics() {
    return this.get('/statistics');
  }

  /**
   * Validate department code availability
   * 
   * @param {string} code - Department code
   * @param {string} [excludeDepartmentId] - Department ID to exclude (for updates)
   * @returns {Promise<{available: boolean}>}
   */
  async validateCode(code, excludeDepartmentId = null) {
    return this.get('/validate-code', {
      params: { code, excludeDepartmentId }
    });
  }
}

// Export singleton instance
const departmentService = new DepartmentService();
export default departmentService;
